/*------------
Version: 2.2
------------*/
import React, { useRef } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import MuiDialog from "@material-ui/core/Dialog";
import { withStyles } from "@material-ui/core/styles";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import CloseIcon from "@material-ui/icons/Close";
import {
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Slide,
  Fade,
} from "@material-ui/core";
import Draggable from "react-draggable";
import Paper from "@material-ui/core/Paper";
import {
  ifNullOrWhiteSpace,
  uuid,
} from "Jarvis/JarvisServices/JarvisCommonService";
import Button from "Jarvis/JarvisComponents/Button";
import Typography from "Jarvis/JarvisComponents/Typography";
import { createMuiTheme, ThemeProvider } from "@material-ui/core/styles";

const styles = (theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(2),
    backgroundColor: theme.palette.primary.main,
    "& h6": {
      color: "white",
    },
  },
  rootWarning: {
    margin: 0,
    padding: theme.spacing(2),
    backgroundColor: theme.palette.warning.main,
    "& h6": {
      color: "white",
    },
  },
  closeButton: {
    // position: "absolute",
    // right: theme.spacing(1),
    // top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
});

const DialogTitle = withStyles(styles)((props) => {
  const { title, classes, onClose, warningColor, titleMoreButtons, ...rest } =
    props;
  return (
    <MuiDialogTitle
      style={{ height: "20px" }}
      disableTypography
      className={clsx(
        "jarvisDialogTitle",
        warningColor ? classes.rootWarning : classes.root
      )}
      {...rest}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography label={title} className="text-white" variant="body2" />
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {titleMoreButtons}
          {onClose ? (
            <IconButton
              aria-label="close"
              className={classes.closeButton}
              onClick={onClose}
            >
              <CloseIcon />
            </IconButton>
          ) : null}
        </div>
      </div>
    </MuiDialogTitle>
  );
});

/**
 * Defined once, at module level, on purpose.
 *
 * Declaring this inside the component body gives it a new identity on every
 * render, and React treats a changed component type as a different component —
 * so MUI tears the dialog down and builds it again each time the parent
 * renders. The content remounts, its state is lost, and anything it loads on
 * mount is loaded twice. Only full-screen dialogs used this path, which is why
 * it stayed hidden.
 */
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function PaperComponent(props) {
  const { dragHandleId, ...paperProps } = props;
  return (
    <Draggable
      handle={`#${dragHandleId}`}
      cancel={'[class*="MuiDialogContent-root"]'}
      // Keep the dialog inside the window. Without bounds it can be dragged
      // above the top of the viewport, which takes the title bar — and with
      // it the close button — somewhere the pointer cannot reach, leaving the
      // dialog open with no way to shut it.
      //
      // "parent" resolves to the dialog root, which is fixed to the viewport.
      // A dialog taller than the window ends up with no vertical room to
      // move and simply stays where it is, which is the safe way to fail.
      bounds="parent"
    >
      <Paper {...paperProps} />
    </Draggable>
  );
}

export default function Dialog({
  acceptBtnClassName,
  acceptBtnFormId,
  acceptBtnText,
  children,
  closeBtnText,
  closeBtnClassName,
  contentText,
  disableBackdropClick,
  disableEscapeKeyDown,
  hasTransition,
  isDraggable,
  fullScreen,
  maxWidth,
  onAcceptBtnClick,
  onClose,
  onCloseBtnClick,
  open,
  refContent,
  showAcceptBtn,
  showCloseBtn,
  showDialogAction,
  showDialogTitle,
  title,
  titleClassName,
  titleMoreButtons,
  titleStyle,
  warningColor,
  width,
  ...rest
}) {
  const dragHandleIdRef = useRef(`draggable-dialog-${uuid()}`);
  const dragHandleId = dragHandleIdRef.current;
  let paperStyle = rest.style ?? {};
  if (width !== undefined) paperStyle.width = width;

  titleStyle = ifNullOrWhiteSpace(titleStyle, { textAlign: "justify" });

  closeBtnText = ifNullOrWhiteSpace(closeBtnText, "Close");
  onCloseBtnClick = onCloseBtnClick ?? onClose;

  acceptBtnText = ifNullOrWhiteSpace(acceptBtnText, "Submit");

  const dialogProps = {
    className: "jarvisDialog",
    disableEscapeKeyDown,
    fullScreen,
    maxWidth,
    open,
    PaperProps: {
      style: paperStyle,
      dragHandleId
    },
    TransitionComponent: fullScreen && hasTransition ? Transition : Fade,
    ...rest,
  };

  if (isDraggable && !fullScreen) dialogProps.PaperComponent = PaperComponent;

  const theme = createMuiTheme({
    zIndex: {
      modal: 9999, // override default 1300
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <MuiDialog
        {...dialogProps}
        onClose={(event, reason) => {
          if (
            (reason === "escapeKeyDown" && !disableEscapeKeyDown) ||
            (reason === "backdropClick" && !disableBackdropClick)
          )
            onClose();
        }}
      >
        {showDialogTitle && (
          <DialogTitle
            title={title}
            style={isDraggable ? { cursor: "move" } : {}}
            warningColor={warningColor}
            onClose={onClose}
            id={dragHandleId}
            titleMoreButtons={titleMoreButtons}
          />
        )}
        <DialogContent
          ref={refContent}
          dividers
          className="jarvisDialogContent"
        >
          {contentText && (
            <DialogContentText>
              <Typography
                style={{ whiteSpace: "pre-wrap" }}
                label={contentText}
              />
            </DialogContentText>
          )}
          {children}
        </DialogContent>
        {showDialogAction && (
          <DialogActions className="jarvisDialogAction">
            {showCloseBtn && (
              <Button
                className={closeBtnClassName}
                variant="outlined"
                size="small"
                label={closeBtnText}
                onClick={onCloseBtnClick}
              />
            )}
            {showAcceptBtn && (
              <Button
                className={acceptBtnClassName}
                variant="contained"
                size="small"
                color="primary"
                label={acceptBtnText}
                onClick={onAcceptBtnClick}
                form={acceptBtnFormId}
                type="submit"
              />
            )}
          </DialogActions>
        )}
      </MuiDialog>
    </ThemeProvider>
  );
}

Dialog.propTypes = {
  disableBackdropClick: PropTypes.bool,
  disableEscapeKeyDown: PropTypes.bool,
  hasTransition: PropTypes.bool,
  isDraggable: PropTypes.bool,
  showAcceptBtn: PropTypes.bool,
  showCloseBtn: PropTypes.bool,
  showDialogAction: PropTypes.bool,
  showDialogTitle: PropTypes.bool,
  titleClassName: PropTypes.string,
  warningColor: PropTypes.bool,
};

Dialog.defaultProps = {
  disableBackdropClick: true,
  disableEscapeKeyDown: false,
  hasTransition: true,
  isDraggable: true,
  maxWidth: false,
  showAcceptBtn: true,
  showCloseBtn: true,
  showDialogAction: false,
  showDialogTitle: true,
  titleClassName: "Jarvis-Font-Size-9rem",
  warningColor: false,
};
